import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../../services/api';
import notificationService from '../../services/notificationService';
import { trackFeature } from '../../utils/analytics';
import { PLATFORMS } from '../../constants/platforms';

const OnboardingWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    platforms: [],
    goals: [],
    notifications: {
      email: true,
      healthAlerts: true,
      revenueUpdates: false,
    },
  });
  const steps = [
    {
      title: 'Welcome to OmniConnX',
      description: 'Let\'s get you set up in just a few steps',
      component: WelcomeStep,
    },
    {
      title: 'Connect Your Platforms',
      description: 'Which platforms do you create content on?',
      component: PlatformsStep,
    },
    {
      title: 'Set Your Goals',
      description: 'What would you like to achieve?',
      component: GoalsStep,
    },
    {
      title: 'Notification Preferences',
      description: 'How would you like to stay updated?',
      component: NotificationsStep,
    },
    {
      title: 'You\'re All Set!',
      description: 'Start managing your creator journey',
      component: CompletionStep,
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      // Persist the notification choices from the wizard (non-blocking on failure —
      // the user can always adjust them later in Settings → Notifications)
      const n = formData.notifications || {};
      await notificationService.updatePreferences({
        emailEnabled: !!n.email,
        healthAlerts: !!n.healthAlerts,
        revenueAlerts: !!n.revenueUpdates,
      }).catch((err) => console.error('Failed to save notification preferences:', err));

      await api.put('/api/v1/auth/onboarding');
      trackFeature('onboarding_complete');
      toast.success('Onboarding completed! Welcome to OmniConnX');
      onComplete();
    } catch (error) {
      toast.error('Failed to complete onboarding');
      console.error('Onboarding error:', error);
    }
  };

  const handleSkip = () => {
    onComplete();
    toast.info('You can complete setup later in Settings');
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {steps[currentStep].title}
            </h2>
            <button
              onClick={handleSkip}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <p className="text-gray-600 dark:text-gray-400">
            {steps[currentStep].description}
          </p>

          {/* Progress Bar */}
          <div className="mt-4 flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full ${
                  index <= currentStep
                    ? 'bg-primary-600'
                    : 'bg-gray-200 dark:bg-dark-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <CurrentStepComponent
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onComplete={handleComplete}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-dark-700 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Skip for now
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <button onClick={handleBack} className="btn btn-secondary">
                Back
              </button>
            )}

            {currentStep < steps.length - 2 && (
              <button onClick={handleNext} className="btn btn-primary">
                Next
              </button>
            )}

            {currentStep === steps.length - 2 && (
              <button onClick={handleNext} className="btn btn-primary">
                Finish
              </button>
            )}

            {currentStep === steps.length - 1 && (
              <button onClick={handleComplete} className="btn btn-primary">
                Get Started
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Step Components
const WelcomeStep = () => (
  <div className="text-center py-8">
    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-600 to-primary-400 rounded-full flex items-center justify-center">
      <span className="text-4xl">🚀</span>
    </div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
      Welcome to OmniConnX!
    </h3>
    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
      Your all-in-one platform for managing content, tracking analytics, and growing your creator business.
    </p>
  </div>
);

const PlatformsStep = ({ formData, updateFormData }) => {
  const togglePlatform = (platformValue) => {
    const current = formData.platforms || [];
    const updated = current.includes(platformValue)
      ? current.filter((p) => p !== platformValue)
      : [...current, platformValue];
    updateFormData('platforms', updated);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {PLATFORMS.map((platform) => {
        const isSelected = formData.platforms?.includes(platform.value);
        const PlatformIcon = platform.icon;
        return (
          <button
            key={platform.value}
            onClick={() => togglePlatform(platform.value)}
            className={`p-6 rounded-lg border-2 transition-all ${
              isSelected
                ? `${platform.borderColor} ${platform.bgColor}`
                : 'border-gray-200 dark:border-dark-700 hover:border-gray-300 dark:hover:border-dark-500'
            }`}
          >
            <div className={`flex justify-center mb-2 ${platform.color}`}>
              <PlatformIcon className="text-4xl w-10 h-10" />
            </div>
            <div className="font-medium text-gray-900 dark:text-white">{platform.label}</div>
            {isSelected && (
              <CheckIcon className={`w-5 h-5 ${platform.color} mx-auto mt-2`} />
            )}
          </button>
        );
      })}
    </div>
  );
};

const GoalsStep = ({ formData, updateFormData }) => {
  const goals = [
    { id: 'GROW_AUDIENCE', label: 'Grow my audience', icon: '📈' },
    { id: 'INCREASE_REVENUE', label: 'Increase revenue', icon: '💰' },
    { id: 'IMPROVE_ENGAGEMENT', label: 'Improve engagement', icon: '💬' },
    { id: 'TRACK_ANALYTICS', label: 'Track analytics', icon: '📊' },
    { id: 'MANAGE_DEALS', label: 'Manage brand deals', icon: '🤝' },
    { id: 'OPTIMIZE_CONTENT', label: 'Optimize content', icon: '✨' },
  ];

  const toggleGoal = (goalId) => {
    const current = formData.goals || [];
    const updated = current.includes(goalId)
      ? current.filter((g) => g !== goalId)
      : [...current, goalId];
    updateFormData('goals', updated);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {goals.map((goal) => {
        const isSelected = formData.goals?.includes(goal.id);
        return (
          <button
            key={goal.id}
            onClick={() => toggleGoal(goal.id)}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              isSelected
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-dark-700 hover:border-primary-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{goal.icon}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{goal.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

const NotificationsStep = ({ formData, updateFormData }) => {
  const updateNotification = (key, value) => {
    updateFormData('notifications', { ...formData.notifications, [key]: value });
  };

  const notifications = [
    { id: 'email', label: 'Email Notifications', description: 'Receive email updates about your account' },
    { id: 'healthAlerts', label: 'Health Alerts', description: 'Get notified when anomalies are detected' },
    { id: 'revenueUpdates', label: 'Revenue Updates', description: 'Weekly revenue reports and reconciliation status' },
  ];

  return (
    <div className="space-y-4">
      {notifications.map((notif) => (
        <div
          key={notif.id}
          className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-dark-700"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{notif.label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{notif.description}</p>
          </div>
          <input
            type="checkbox"
            checked={formData.notifications?.[notif.id] || false}
            onChange={(e) => updateNotification(notif.id, e.target.checked)}
            className="w-5 h-5 rounded text-primary-600"
          />
        </div>
      ))}
    </div>
  );
};

const CompletionStep = () => (
  <div className="text-center py-8">
    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
      <CheckIcon className="w-12 h-12 text-white" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
      You're Ready to Go!
    </h3>
    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
      Your account is all set up. Start exploring OmniConnX and take control of your creator journey.
    </p>
    <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
      <div className="flex items-center gap-2">
        <CheckIcon className="w-4 h-4 text-green-500" />
        <span>Platforms connected</span>
      </div>
      <div className="flex items-center gap-2">
        <CheckIcon className="w-4 h-4 text-green-500" />
        <span>Goals set</span>
      </div>
      <div className="flex items-center gap-2">
        <CheckIcon className="w-4 h-4 text-green-500" />
        <span>Notifications configured</span>
      </div>
    </div>
  </div>
);

export default OnboardingWizard;
